import React, { useEffect, useState, useRef } from "react";
import { BotFilteringNodeData, HandleConfig } from "../../types";
import { Position } from "reactflow";
import ConfigBoxToolbar from "../../components/shared/ConfigBoxToolbar";
import LabelWithVisibilityToggle, { VisibilityOption } from "../../components/shared/LabelWithVisibilityToggle";
import AutoComplete from "../../components/shared/AutoComplete";
import HandleManager from "../../components/shared/HandleManager";

interface BotFilteringConfigBoxProps {
  nodeId: string;
  data: BotFilteringNodeData;
  onUpdate?: (data: Partial<BotFilteringNodeData>) => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  onDetachFromGroup?: () => void; // Add prop
  isAdvancedMode?: boolean;
  portalRoot?: HTMLElement | null;
}

const BOT_FILTERING_SUGGESTIONS = [
  { value: "Blacklist Bot Filter", label: "Blacklist Bot Filter" },
  { value: "IP-based Bot Filter", label: "IP-based Bot Filter" },
  { value: "Custom Bot Filter", label: "Custom Bot Filter" },
];

export default function BotFilteringConfigBox({
  nodeId,
  data,
  onUpdate,
  onDelete,
  onDuplicate,
  onDetachFromGroup, // Destructure prop
  portalRoot,
  isAdvancedMode = false,
}: BotFilteringConfigBoxProps) {
  // Initialize state with props
  const [name, setName] = useState(data.name || "");
  const [botDefinition, setBotDefinition] = useState(data.botDefinition || "");
  const [notes, setNotes] = useState(data.notes || "");

  const [nameVisibility, setNameVisibility] = useState<VisibilityOption>(
    data.nameVisibility === "hidden" ? "hidden" : "visible"
  );
  const [notesVisibility, setNotesVisibility] = useState<VisibilityOption>(
    data.notesVisibility === "hidden" ? "hidden" : "visible"
  );

  const defaultHandles: HandleConfig[] = [
    { id: 'default-target', type: 'target', position: Position.Left, color: '#22c55e' },
    { id: 'accepted-output', type: 'source', position: Position.Right, color: '#3b82f6' },
    { id: 'rejected-output', type: 'source', position: Position.Right, color: '#ef4444' },
  ];

  const [handles, setHandles] = useState<HandleConfig[]>(
    Array.isArray(data.handles) ? [...data.handles] : defaultHandles
  );

  const isFirstRender = useRef(true);
  const prevUpdateRef = useRef("");
  const prevPropsRef = useRef("");

  useEffect(() => {
    const incomingProps = JSON.stringify({
      name: data.name || "",
      botDefinition: data.botDefinition || "",
      notes: data.notes || "",
      nameVisibility: data.nameVisibility === "hidden" ? "hidden" : "visible",
      notesVisibility: data.notesVisibility === "hidden" ? "hidden" : "visible",
      handles: Array.isArray(data.handles) ? data.handles : defaultHandles,
    });

    if (incomingProps !== prevPropsRef.current) {
      prevPropsRef.current = incomingProps;

      setName(data.name || "");
      setBotDefinition(data.botDefinition || "");
      setNotes(data.notes || "");
      setNameVisibility(data.nameVisibility === "hidden" ? "hidden" : "visible");
      setNotesVisibility(data.notesVisibility === "hidden" ? "hidden" : "visible");

      if (Array.isArray(data.handles)) {
        setHandles([...data.handles]);
      } else {
        setHandles(defaultHandles);
      }
    }

    if (isFirstRender.current) {
      isFirstRender.current = false;
    }
  }, [data]);

  useEffect(() => {
    if (isFirstRender.current) return;

    const outgoingUpdate = JSON.stringify({
      name,
      botDefinition,
      notes,
      nameVisibility,
      notesVisibility,
      handles,
    });

    if (outgoingUpdate !== prevUpdateRef.current && onUpdate) {
      prevUpdateRef.current = outgoingUpdate;
      onUpdate({
        name,
        botDefinition,
        notes,
        nameVisibility,
        notesVisibility,
        handles,
      });
    }
  }, [
    name,
    botDefinition,
    notes,
    nameVisibility,
    notesVisibility,
    handles,
    onUpdate
  ]);
  
  // Toggle visibility setting
  const toggleVisibility = (
    current: VisibilityOption,
    setter: React.Dispatch<React.SetStateAction<VisibilityOption>>
  ) => {
    const nextVisibility = current === "hidden" ? "visible" : "hidden";
    setter(nextVisibility);
  };

  return (
    <div className="space-y-3 relative">
      {/* Add the toolbar */}
      <ConfigBoxToolbar
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onDetachFromGroup={onDetachFromGroup} // Pass prop down
        isAdvancedMode={isAdvancedMode}
      />

      <div className="pt-1">
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 mt-3">
          Bot Filter Name
        </label>
        <AutoComplete
          value={name}
          onChange={(value: string) => {
            setName(value);
          }}
          onSelect={(option) => {
            if (typeof option === 'object') {
              setName(option.value);
            } else {
              setName(option);
            }
          }}
          options={BOT_FILTERING_SUGGESTIONS}
          placeholder="Select or enter bot filter name"
          dropdownClassName="absolute z-[100]"
          portalContainer={portalRoot}
        />
      </div>

      <div>
        <LabelWithVisibilityToggle
          label="Notes"
          visibility={notesVisibility}
          onToggleVisibility={() => toggleVisibility(notesVisibility, setNotesVisibility)}
          className="mb-1"
        />
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add notes about this bot filter (optional)"
          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-700 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out resize-y min-h-[80px] max-h-[400px]"
          maxLength={500} // Limit to 500 characters
        />
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
          {notes.length}/500
        </div>
      </div>
      {/* --- Handle Manager --- */}
      <div>
        <HandleManager
          nodeId={nodeId}
          handles={handles}
          onChange={setHandles}
          maxHandles={6}
          portalRoot={portalRoot}
        />
      </div>
    </div>
  );
}
